import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Stack,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  formatPlanType,
  HISTORY_TABLE_HEADERS,
  HISTORY_CLICK_HINT,
} from '../constants/inspectionStatusConfigs';
import {
  PaymentStatusChip,
  InspectionStatusChip,
  InspectionValueChip,
} from './InspectionHistoryChips';

const headerCellSx = {
  fontWeight: 600,
  borderBottom: 'none',
  whiteSpace: 'nowrap',
  px: { xs: 1, sm: 2 },
};

const bodyCellSx = {
  borderBottom: 'none',
  py: 2.5,
};

const InspectionHistoryTable = ({
  inspections,
  pageItems,
  onRowClick,
  page,
  totalPages,
  onPageChange,
  paginationSize = 'medium',
}) => {
  const hasInspections = inspections && inspections.length > 0;

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#FFFFFF' }}>
              {HISTORY_TABLE_HEADERS.map((header) => (
                <TableCell key={header} align="center" sx={headerCellSx}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {hasInspections ? (
              pageItems.map((inspection, index) => (
                <TableRow
                  key={inspection.id}
                  onClick={() => onRowClick(inspection)}
                  sx={{
                    bgcolor: index % 2 === 0 ? '#FFFFFF' : '#EEF2F7',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(1, 47, 71, 0.05)',
                      cursor: 'pointer',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
                    },
                  }}
                >
                  <TableCell align="center" sx={bodyCellSx}>
                    <Typography sx={{ fontWeight: 500 }}>{inspection.id}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formatPlanType(inspection.plan_type)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    <Typography>{inspection.vehicle}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    <Typography>
                      {inspection.created_at
                        ? format(parseISO(inspection.created_at), 'dd/MM/yyyy HH:mm', {
                            locale: ptBR,
                          })
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    <PaymentStatusChip status={inspection.status} />
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    <InspectionStatusChip inspectionStatus={inspection.inspection_status} />
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    <InspectionValueChip value={inspection.value} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={HISTORY_TABLE_HEADERS.length} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">Nenhuma vistoria encontrada</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {hasInspections && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 2,
            color: '#012F47',
            fontStyle: 'italic',
          }}
        >
          {HISTORY_CLICK_HINT}
        </Typography>
      )}

      {hasInspections && (
        <Stack spacing={2} alignItems="center" sx={{ mt: 3, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={onPageChange}
            color="primary"
            size={paginationSize}
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#012F47',
              },
              '& .Mui-selected': {
                backgroundColor: '#012F47 !important',
                color: 'white',
              },
            }}
          />
        </Stack>
      )}
    </>
  );
};

export default InspectionHistoryTable;
